import { useEffect, useRef, useState } from "react";
import { fetchActivePromotion, type ActivePromotion } from "@/lib/active-promotion";
import { trackCustom } from "@/lib/meta-pixel";

export type PromotionPhase = "launch" | "last_chance" | "off";

export interface LaunchOfferStatus {
  /** True while any promotional copy should be shown (launch OR last_chance). */
  active: boolean;
  expired: boolean;
  /** Milliseconds remaining until end_at. 0 during last_chance (no countdown). */
  msRemaining: number;
  phase: PromotionPhase;
  /** End instant of the launch countdown in ms (0 when inactive). */
  endMs: number;
  /** ISO end date of the launch countdown. */
  endISO: string;
  /** Underlying promotion row, or null when inactive. */
  promotion: ActivePromotion | null;
  /** Resolved headline for the current phase. */
  headline: string;
  /** Resolved supporting text for the current phase. */
  supportingText: string;
  /** CTA destination. */
  ctaUrl: string;
  /** Terms link. */
  termsUrl: string;
  /** Discount %. */
  discountPercentage: number;
  /** Promotion slug (stable id), empty when inactive. */
  promotionId: string;
  /** Internal admin name. */
  promotionName: string;
}

// ---- module-level cache (one fetch per browser session) ----
let cache: ActivePromotion | null | undefined = undefined;
let inFlight: Promise<ActivePromotion | null> | null = null;
const subscribers = new Set<(p: ActivePromotion | null) => void>();
let pollTimer: ReturnType<typeof setInterval> | null = null;

function ensurePromotion(): Promise<ActivePromotion | null> {
  if (cache !== undefined) return Promise.resolve(cache);
  if (!inFlight) {
    inFlight = fetchActivePromotion().then((p) => {
      cache = p;
      inFlight = null;
      return p;
    });
  }
  return inFlight;
}

function ensurePolling() {
  if (pollTimer) return;
  pollTimer = setInterval(() => {
    fetchActivePromotion().then((p) => {
      const changed = JSON.stringify(cache) !== JSON.stringify(p);
      cache = p;
      if (changed) for (const fn of subscribers) fn(p);
    });
  }, 60_000);
}

const INACTIVE: LaunchOfferStatus = {
  active: false,
  expired: false,
  msRemaining: 0,
  phase: "off",
  endMs: 0,
  endISO: "",
  promotion: null,
  headline: "",
  supportingText: "",
  ctaUrl: "/apply",
  termsUrl: "/launch-offer-terms",
  discountPercentage: 0,
  promotionId: "",
  promotionName: "",
};

function computeStatus(p: ActivePromotion | null, now = Date.now()): LaunchOfferStatus {
  if (!p) return INACTIVE;
  const endMs = new Date(p.end_at).getTime();
  if (!Number.isFinite(endMs)) return INACTIVE;
  if (now < endMs) {
    return {
      active: true,
      expired: false,
      msRemaining: Math.max(0, endMs - now),
      phase: "launch",
      endMs,
      endISO: p.end_at,
      promotion: p,
      headline: p.public_headline,
      supportingText: p.supporting_text,
      ctaUrl: p.cta_url,
      termsUrl: p.terms_url,
      discountPercentage: p.discount_percentage,
      promotionId: p.slug,
      promotionName: p.name,
    };
  }
  if (p.last_chance_enabled) {
    return {
      active: true,
      expired: false,
      msRemaining: 0,
      phase: "last_chance",
      endMs,
      endISO: p.end_at,
      promotion: p,
      headline: p.last_chance_headline || p.public_headline,
      supportingText: p.last_chance_supporting_text || p.supporting_text,
      ctaUrl: p.cta_url,
      termsUrl: p.terms_url,
      discountPercentage: p.discount_percentage,
      promotionId: p.slug,
      promotionName: p.name,
    };
  }
  return { ...INACTIVE, expired: true };
}

/**
 * Reads the active promotion from the admin-managed `promotions` table
 * and resolves its current phase. Re-polls every 60s to pick up admin
 * edits without a redeploy. Ticks once per second to flip the phase at
 * `end_at` and to advance per-second countdowns rendered downstream.
 */
export function useLaunchOfferStatus(): LaunchOfferStatus {
  const [promo, setPromo] = useState<ActivePromotion | null>(() =>
    cache === undefined ? null : cache,
  );
  const [, setTick] = useState(0);
  const lastPhaseRef = useRef<PromotionPhase | null>(null);

  useEffect(() => {
    let cancelled = false;
    ensurePromotion().then((p) => {
      if (!cancelled) setPromo(p);
    });
    const onUpdate = (p: ActivePromotion | null) => setPromo(p);
    subscribers.add(onUpdate);
    ensurePolling();
    return () => {
      subscribers.delete(onUpdate);
    };
  }, []);

  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => (n + 1) % 1_000_000), 1000);
    return () => window.clearInterval(id);
  }, []);

  const status = computeStatus(promo);

  // Fire `promotion_phase_started` once per browser when we first see
  // the launch → last_chance flip.
  useEffect(() => {
    if (status.phase === "last_chance" && lastPhaseRef.current !== "last_chance") {
      lastPhaseRef.current = "last_chance";
      try {
        trackCustom(
          "promotion_phase_started",
          {
            promotion_id: status.promotionId,
            promotion_phase: "last_chance",
          },
          { dedupeKey: `promo_phase_started_last_chance_${status.promotionId}` },
        );
      } catch {
        /* never block UX on tracking */
      }
    } else if (status.phase !== "last_chance") {
      lastPhaseRef.current = status.phase;
    }
  }, [status.phase, status.promotionId]);

  return status;
}
